import { useEffect, useId, useState } from "react";

type State = { svg: string; error: string };

let configured: Promise<typeof import("mermaid").default> | null = null;

/** Mermaid is heavy, so it is loaded the first time a diagram is actually rendered. */
function loadMermaid() {
  configured ??= import("mermaid").then(({ default: mermaid }) => {
    mermaid.initialize({
      startOnLoad: false,
      securityLevel: "strict",
      theme: "dark",
      fontFamily: '"SFMono-Regular", Consolas, "Liberation Mono", monospace',
      themeVariables: {
        background: "#11151e",
        primaryColor: "#242b3a",
        primaryTextColor: "#f5f6fa",
        primaryBorderColor: "#a970ff",
        lineColor: "#7e8aa2",
        secondaryColor: "#1d2430",
        tertiaryColor: "#181d27",
        // Mermaid's dark theme still paints edge labels, notes and sequence
        // actors on near-white plates, which are unreadable on this palette.
        edgeLabelBackground: "#1d2430",
        noteBkgColor: "#242b3a",
        noteTextColor: "#f5f6fa",
        noteBorderColor: "#a970ff",
        actorBkg: "#242b3a",
        actorBorder: "#a970ff",
        actorTextColor: "#f5f6fa",
        actorLineColor: "#7e8aa2",
        signalColor: "#a2a8b8",
        signalTextColor: "#f5f6fa",
        labelBoxBkgColor: "#242b3a",
        labelBoxBorderColor: "#a970ff",
        labelTextColor: "#f5f6fa",
        loopTextColor: "#f5f6fa",
        sequenceNumberColor: "#11151e"
      }
    });
    return mermaid;
  });
  return configured;
}

export function Mermaid({ code }: { code: string }) {
  const [{ svg, error }, setState] = useState<State>({ svg: "", error: "" });
  const domId = `mermaid-${useId().replace(/[^a-zA-Z0-9]/g, "")}`;

  useEffect(() => {
    let active = true;
    const source = code.trim();
    if (!source) {
      setState({ svg: "", error: "" });
      return;
    }
    void (async () => {
      try {
        const mermaid = await loadMermaid();
        // Parsing first keeps a syntax error from injecting Mermaid's own error card into the page.
        await mermaid.parse(source);
        const { svg: rendered } = await mermaid.render(domId, source);
        if (active) setState({ svg: rendered, error: "" });
      } catch (cause) {
        if (active) setState({ svg: "", error: cause instanceof Error ? cause.message : "Unable to draw this diagram." });
      }
    })();
    return () => {
      active = false;
      document.getElementById(domId)?.remove();
      document.getElementById(`d${domId}`)?.remove();
    };
  }, [code, domId]);

  if (error) {
    return (
      <div className="md-diagram md-diagram-error">
        <p role="status">Diagram error: {error}</p>
        <pre>
          <code>{code}</code>
        </pre>
      </div>
    );
  }

  return (
    <div
      className="md-diagram"
      role="img"
      aria-label="Diagram"
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
}
